import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiArrowLeft, FiEdit2, FiTrash2, FiCalendar, FiUser, 
  FiTrendingUp, FiTrendingDown, FiMinus, FiActivity,
  FiTarget, FiCheckCircle, FiAlertCircle
} from 'react-icons/fi';
import qualityMetricService from '../../services/qualityMetricService';

const QualityMetricView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [metric, setMetric] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQualityMetric();
  }, [id]);

  const fetchQualityMetric = async () => {
    try {
      setLoading(true);
      const response = await qualityMetricService.getQualityMetric(id);
        const data = response.data;
      setMetric(data);
    } catch (error) {
      toast.error('Failed to fetch quality metric');
      console.error('Error fetching quality metric:', error);
      navigate('/app/quality-metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this quality metric?')) {
      return;
    }

    try {
      await qualityMetricService.deleteQualityMetric(id);
      toast.success('Quality metric deleted successfully');
      navigate('/app/quality-metrics');
    } catch (error) {
      toast.error('Failed to delete quality metric');
      console.error('Error deleting quality metric:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Excellent': 'bg-green-100 text-green-800 border-green-200',
      'On Track': 'bg-blue-100 text-blue-800 border-blue-200',
      'At Risk': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Off Track': 'bg-red-100 text-red-800 border-red-200',
      'Not Measured': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'bg-red-100 text-red-800 border-red-200',
      'High': 'bg-orange-100 text-orange-800 border-orange-200',
      'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Low': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'Improving':
        return <FiTrendingUp className="text-green-600" size={24} />;
      case 'Declining':
        return <FiTrendingDown className="text-red-600" size={24} />;
      case 'Stable':
        return <FiMinus className="text-blue-600" size={24} />;
      default:
        return <FiActivity className="text-gray-600" size={24} />;
    }
  };

  const calculateAchievement = () => {
    if (!metric.target_value || metric.target_value === 0) return null;
    return ((metric.actual_value / metric.target_value) * 100).toFixed(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading quality metric...</div>
      </div>
    );
  }

  if (!metric) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Quality metric not found</div>
      </div>
    );
  }

  const achievement = calculateAchievement();

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/quality-metrics')}
            className="text-gray-600 hover:text-gray-900"
          >
            <FiArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{metric.metric_name}</h1>
            <p className="text-gray-600 mt-1">{metric.project?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/app/quality-metrics/${id}/edit`}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiEdit2 /> Edit
          </Link>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <FiTrash2 /> Delete
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Target Value</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {metric.target_value} {metric.measurement_unit}
              </div>
            </div>
            <FiTarget className="text-blue-500" size={32} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Actual Value</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {metric.actual_value || 'N/A'} {metric.actual_value ? metric.measurement_unit : ''}
              </div>
            </div>
            <FiCheckCircle className="text-green-500" size={32} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Achievement</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {achievement ? `${achievement}%` : 'N/A'}
              </div>
            </div>
            <FiAlertCircle className="text-purple-500" size={32} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Trend</div>
              <div className="text-lg font-bold text-gray-900 mt-1">
                {metric.trend}
              </div>
            </div>
            {getTrendIcon(metric.trend)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Metric Type</div>
                <div className="text-gray-900 font-medium mt-1">{metric.metric_type}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Category</div>
                <div className="text-gray-900 font-medium mt-1">{metric.category}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Priority</div>
                <div className="mt-1">
                  <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full border ${getPriorityColor(metric.priority)}`}>
                    {metric.priority}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Status</div>
                <div className="mt-1">
                  <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full border ${getStatusColor(metric.status)}`}>
                    {metric.status}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Measurement Period</div>
                <div className="text-gray-900 font-medium mt-1">{metric.measurement_period}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Measurement Date</div>
                <div className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                  <FiCalendar className="text-gray-400" />
                  {formatDate(metric.measurement_date)}
                </div>
              </div>
            </div>
          </div>

          {/* Thresholds */}
          {(metric.threshold_min || metric.threshold_max) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Thresholds</h2>
              <div className="grid grid-cols-2 gap-4">
                {metric.threshold_min && (
                  <div>
                    <div className="text-sm text-gray-600">Minimum Threshold</div>
                    <div className="text-gray-900 font-medium mt-1">
                      {metric.threshold_min} {metric.measurement_unit}
                    </div>
                  </div>
                )}
                {metric.threshold_max && (
                  <div>
                    <div className="text-sm text-gray-600">Maximum Threshold</div>
                    <div className="text-gray-900 font-medium mt-1">
                      {metric.threshold_max} {metric.measurement_unit}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {metric.description && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{metric.description}</p>
            </div>
          )}

          {/* Data Source & Calculation */}
          {(metric.data_source || metric.calculation_method) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Measurement Details</h2>
              <div className="space-y-4">
                {metric.data_source && (
                  <div>
                    <div className="text-sm text-gray-600">Data Source</div>
                    <div className="text-gray-900 mt-1">{metric.data_source}</div>
                  </div>
                )}
                {metric.calculation_method && (
                  <div>
                    <div className="text-sm text-gray-600">Calculation Method</div>
                    <div className="text-gray-900 mt-1 font-mono text-sm bg-gray-50 p-3 rounded">
                      {metric.calculation_method}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Items */}
          {metric.action_items && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Action Items</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{metric.action_items}</p>
            </div>
          )}

          {/* Notes */}
          {metric.notes && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{metric.notes}</p>
            </div>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Responsible User */}
          {metric.responsibleUser  && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Responsible User</h2>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <FiUser className="text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {metric.responsibleUser.first_name} {metric.responsibleUser.last_name}
                  </div>
                  <div className="text-sm text-gray-600">{metric.responsibleUser.email}</div>
                </div>
              </div>
            </div>
          )}

          {/* Project Info */}
          {metric.project && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Project</h2>
              <div>
                <div className="font-medium text-gray-900">{metric.project.name}</div>
                {metric.project.code && (
                  <div className="text-sm text-gray-600 mt-1">Code: {metric.project.code}</div>
                )}
                {metric.project.status && (
                  <div className="mt-2">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {metric.project.status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h2>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-gray-600">Status</div>
                <div className="text-gray-900 font-medium mt-1">
                  {metric.is_active ? (
                    <span className="text-green-600">Active</span>
                  ) : (
                    <span className="text-red-600">Inactive</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-gray-600">Created</div>
                <div className="text-gray-900 mt-1">{formatDate(metric.created_at)}</div>
              </div>
              <div>
                <div className="text-gray-600">Last Updated</div>
                <div className="text-gray-900 mt-1">{formatDate(metric.updated_at)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualityMetricView;

